import React, { useState, useRef, useCallback, useId } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Modal, ModalTitle, ModalBody, ModalFooter, ModalFooterAlignment } from 'design-system/src/components/Modal';
import Button from 'design-system/src/components/Button/Button';
import { DrawInputField, DrawingInputFieldRef } from 'design-system/src/components/DrawInputField/DrawInputField';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';
import { useFormFillerSignature } from './hooks';
import { TabList } from 'design-system/src/components/TabList/TabList';
import { standardTransitionStyles } from 'design-system/src/sharedStyles';
import { focusVisibleStyles } from 'design-system/src/sharedStyles';
import * as Sentry from '@sentry/react';
import { resizeDrawnSignature } from '../../lib/resizeDrawnSignature';
import UploadIcon from 'design-system/src/assets/icons/upload.svg?react';

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FileUploadContainer = styled.div`
  cursor: pointer;
  border: 2px solid var(--grayscale-100);
  border-radius: var(--border-radius-medium);
  padding: 32px;
  text-align: center;
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: var(--surface-100);
  ${standardTransitionStyles}
  ${focusVisibleStyles}
`;

const FileUploadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UploadButton = styled(Button)`
  background-color: var(--white);
  &:hover {
    background-color: var(--white);
    border-color: var(--grayscale-100);
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 200px;
  border: 1px solid var(--grayscale-100);
  border-radius: 4px;
`;

const CroppingContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CroppingImage = styled.img`
  max-width: 100%;
  max-height: 400px;
  display: block;
`;

const BORDER_RADIUS = 'var(--border-radius-medium)';
const UploadToolbar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-radius: ${BORDER_RADIUS} ${BORDER_RADIUS} 0 0;
  background-color: var(--surface-200);
  padding: 8px;
  border: 1px solid var(--grayscale-100);
  border-bottom: none;
`;

const UploadControls = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
`;

const SecondaryToolbarButton = styled(Button)`
  background-color: var(--white);

  &:hover {
    background-color: var(--white);
    border-color: var(--grayscale-100);
  }
`;

const CroppingWrapper = styled.div`
  border: 1px solid var(--grayscale-100);
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  .ReactCrop {
    position: relative;
  }

  .ReactCrop__crop-selection {
    background: transparent;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.8);
  }
`;

const PreviewWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--grayscale-100);
  border-radius: 0 0 ${BORDER_RADIUS} ${BORDER_RADIUS};
  background-color: var(--surface-100);
  padding: 24px;
`;

const SpacedContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export interface SignatureModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;
  /**
   * Called when the modal should be closed
   */
  onClose: () => void;
  /**
   * Called when a signature is successfully submitted
   */
  onSignatureSubmitted: (info: { previewUrl: string; s3Key: string }) => void;
  /**
   * Whether to show the upload tab
   */
  allowUpload?: boolean;
  width: number;
  height: number;
}

type TabType = 'draw' | 'upload';

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];
// Used in user-facing messages, should stay in sync with the constant above.
const ALLOWED_FILE_TYPES_MESSAGE = 'PNG and JPEG';

// Constants for image processing
const TARGET_ASPECT_RATIO = 3; // 3:1 ratio for signatures
const SIGNATURE_TARGET_HEIGHT = 150;
const SIGNATURE_TARGET_WIDTH = SIGNATURE_TARGET_HEIGHT * TARGET_ASPECT_RATIO;

/**
 * Helper function to resize and compress an image file
 */
const processImageFile = (
  file: File,
  maxWidth: number = SIGNATURE_TARGET_WIDTH,
  maxHeight: number = SIGNATURE_TARGET_HEIGHT,
): Promise<File> => {
  const objectUrl = URL.createObjectURL(file);
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    const img = new Image();

    img.onload = () => {
      const { width: originalWidth, height: originalHeight } = img;

      // Calculate scale to fit within max dimensions while preserving aspect ratio
      const scaleX = maxWidth / originalWidth;
      const scaleY = maxHeight / originalHeight;
      const scale = Math.min(scaleX, scaleY, 1); // Don't upscale

      const finalWidth = Math.round(originalWidth * scale);
      const finalHeight = Math.round(originalHeight * scale);

      canvas.width = finalWidth;
      canvas.height = finalHeight;

      // Draw and compress
      ctx.drawImage(img, 0, 0, finalWidth, finalHeight);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const processedFile = new File([blob], 'signature.png', {
              type: 'image/png',
              lastModified: Date.now(),
            });
            resolve(processedFile);
          } else {
            resolve(file); // Fallback to original
          }
          URL.revokeObjectURL(objectUrl);
        },
        'image/png',
        0.9 /* quality */,
      );
    };

    img.src = objectUrl;
  });
};

/**
 * Helper function to crop an image using canvas
 */
const getCroppedImage = (image: HTMLImageElement, crop: Crop): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    if (!crop.width || !crop.height) {
      // If no crop, convert the image to a file and process it
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;

      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      ctx.drawImage(image, 0, 0);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const imageFile = new File([blob], 'signature.png', {
              type: 'image/png',
              lastModified: Date.now(),
            });
            processImageFile(imageFile).then(resolve);
          } else {
            reject(new Error('Canvas toBlob returned null'));
          }
        },
        'image/png',
        0.9 /* quality */,
      );
      return;
    }

    // Get the actual displayed dimensions of the image element
    const displayedWidth = image.width;
    const displayedHeight = image.height;

    // Calculate scale factors from displayed size to natural size
    const scaleX = image.naturalWidth / displayedWidth;
    const scaleY = image.naturalHeight / displayedHeight;

    // Convert crop coordinates from percentages to pixels
    const cropPixelX = (crop.x / 100) * displayedWidth;
    const cropPixelY = (crop.y / 100) * displayedHeight;
    const cropPixelWidth = (crop.width / 100) * displayedWidth;
    const cropPixelHeight = (crop.height / 100) * displayedHeight;

    // Convert crop coordinates from display pixels to natural image pixels
    const sourceX = cropPixelX * scaleX;
    const sourceY = cropPixelY * scaleY;
    const sourceWidth = cropPixelWidth * scaleX;
    const sourceHeight = cropPixelHeight * scaleY;

    // Set canvas dimensions to the desired output size (in pixels)
    canvas.width = Math.round(cropPixelWidth);
    canvas.height = Math.round(cropPixelHeight);

    ctx.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const croppedFile = new File([blob], 'signature.png', {
            type: 'image/png',
            lastModified: Date.now(),
          });
          resolve(croppedFile);
        } else {
          reject(new Error('Canvas toBlob returned null'));
        }
      },
      'image/png',
      0.9 /* quality */,
    );
  });
};

/**
 * Modal for creating signatures by drawing or uploading files
 */
export function SignatureModal({
  isOpen,
  onClose,
  onSignatureSubmitted,
  allowUpload = false,
  width,
  height,
}: SignatureModalProps) {
  const tabListId = useId();
  const tabPanelId = useId();
  const [activeTab, setActiveTab] = useState<TabType>('draw');
  const [hasDrawn, setHasDrawn] = useState(false);
  const [pendingSignatureInfo, setPendingSignatureInfo] = useState<{ previewUrl: string; s3Key: string } | null>(null);

  const drawingInputFieldRef = useRef<DrawingInputFieldRef>(null);
  const { setSignature, uploadSignature } = useFormFillerSignature();

  const handleDrawingChange = useCallback((data: number[][]) => {
    if (data.length > 0) {
      setHasDrawn(true);
    }
  }, []);
  const aspectRatio = `${width} / ${height}`;

  const handleClearDrawing = useCallback(() => {
    setHasDrawn(false);
  }, []);

  const handleAcceptAndSign = useCallback(async () => {
    if (activeTab === 'draw') {
      if (!hasDrawn || !drawingInputFieldRef.current?.svg) {
        return;
      }

      try {
        const svgElement = drawingInputFieldRef.current.svg;

        // Convert SVG to optimized PNG
        const pngFile = await resizeDrawnSignature(svgElement, {
          targetWidth: SIGNATURE_TARGET_WIDTH,
          targetHeight: SIGNATURE_TARGET_HEIGHT,
          fileName: `signature-${Date.now()}.png`,
        });

        // Upload the PNG file through the same pipeline as Upload tab
        const { previewUrl: uploadedFileUrl, s3Key: s3Key } = await uploadSignature.mutateAsync(pngFile);

        onSignatureSubmitted({ previewUrl: uploadedFileUrl, s3Key: s3Key });
        setSignature({ previewUrl: uploadedFileUrl, s3Key: s3Key });
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            context: 'Error occurred while processing drawn signature',
          },
        });
        toast.custom(() => <StandardErrorToast />);
      }
    } else if (activeTab === 'upload') {
      if (!pendingSignatureInfo) {
        toast.custom(() => <Toast variant="error">No signature to upload</Toast>);
        return;
      }
      onSignatureSubmitted(pendingSignatureInfo);
      setSignature(pendingSignatureInfo);
    }
  }, [activeTab, hasDrawn, pendingSignatureInfo, uploadSignature, onSignatureSubmitted, setSignature]);

  const canAccept = activeTab === 'draw' ? hasDrawn : !!pendingSignatureInfo;

  const tabs = allowUpload
    ? [
        { value: 'draw', label: 'Draw' },
        { value: 'upload', label: 'Upload' },
      ]
    : [{ value: 'draw', label: 'Draw' }];

  const handleSignatureProcessing = useCallback(
    async (file: File) => {
      const { previewUrl: uploadedFileUrl, s3Key: s3Key } = await uploadSignature.mutateAsync(file);
      setPendingSignatureInfo({ previewUrl: uploadedFileUrl, s3Key: s3Key });
    },
    [uploadSignature],
  );
  const handleClearUpload = useCallback(() => {
    setPendingSignatureInfo(null);
  }, []);

  if (!isOpen) return null;

  return (
    <Modal onClose={onClose} maxWidth="600px">
      <ModalTitle>Add signature</ModalTitle>

      <ModalBody>
        <SpacedContent>
          <TabList
            tabs={tabs}
            selectedValue={activeTab}
            onSelect={(value) => setActiveTab(value as TabType)}
            tabListId={tabListId}
            tabPanelId={tabPanelId}
            ariaLabel="Signature options"
          />
          <div role="tabpanel" id={tabPanelId} aria-labelledby={tabListId}>
            {activeTab === 'draw' && (
              <TabContent>
                <DrawInputField
                  label="Signature"
                  hideLabel
                  drawingInputFieldRef={drawingInputFieldRef}
                  onDrawingChange={handleDrawingChange}
                  onClearDrawing={handleClearDrawing}
                  aspectRatio={aspectRatio}
                />
              </TabContent>
            )}

            {activeTab === 'upload' && allowUpload && (
              <UploadTab
                previewUrl={pendingSignatureInfo?.previewUrl ?? null}
                handleSignatureProcessing={handleSignatureProcessing}
                onClearUpload={handleClearUpload}
              />
            )}
          </div>
          <ElectronicSignatureInfo />
        </SpacedContent>
      </ModalBody>
      <ActionsFooter canAccept={canAccept} onClose={onClose} handleAcceptAndSign={handleAcceptAndSign} />
    </Modal>
  );
}

const ElectronicSignatureInfoList = styled.ul`
  margin: 0;
`;

const ElectronicSignatureInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

function ElectronicSignatureInfo() {
  return (
    <ElectronicSignatureInfoContainer>
      <Typography styledAs="bodyExtraSmallDMSans" color="var(--grayscale-600)">
        By continuing, you agree that:
        <ElectronicSignatureInfoList>
          <li>
            <Typography styledAs="bodyExtraSmallDMSans" color="var(--grayscale-600)">
              Your electronic signature has the same legal effect as a handwritten one.
            </Typography>
          </li>
          <li>
            You consent to receive, view, and sign all documents, disclosures, and records related to the services
            electronically.
          </li>
          <li>
            You meet the minimum hardware and software requirements necessary to access and retain these electronic
            records.
          </li>
          <li>You can access and keep a copy of these records for your files.</li>
          <li>
            You understand you may request paper copies or withdraw consent at any time by contacting Tandem Technology,
            Inc.
          </li>
          <li>
            You affirm that you have reviewed the{' '}
            <Button
              textStyledAs="bodyExtraSmallDMSans"
              variant="link"
              target="_blank"
              href="https://www.withtandem.com/privacy-policy"
            >
              Tandem Privacy Policy
            </Button>{' '}
            and{' '}
            <Button
              textStyledAs="bodyExtraSmallDMSans"
              variant="link"
              target="_blank"
              href="https://www.withtandem.com/tou"
            >
              Terms of Service
            </Button>
            .
          </li>
        </ElectronicSignatureInfoList>
      </Typography>
    </ElectronicSignatureInfoContainer>
  );
}

function ActionsFooter({
  canAccept,
  onClose,
  handleAcceptAndSign,
}: {
  canAccept: boolean;
  onClose: () => void;
  handleAcceptAndSign: () => void;
}) {
  const { uploadSignature } = useFormFillerSignature();
  return (
    <ModalFooter align={ModalFooterAlignment.SpaceBetween}>
      <Button variant="secondary" onClick={onClose} disabled={uploadSignature.isPending}>
        Cancel
      </Button>
      <Button
        onClick={handleAcceptAndSign}
        disabled={!canAccept || uploadSignature.isPending}
        isLoading={uploadSignature.isPending}
      >
        Accept and sign
      </Button>
    </ModalFooter>
  );
}

function UploadTab({
  previewUrl,
  handleSignatureProcessing,
  onClearUpload,
}: {
  previewUrl: string | null;
  handleSignatureProcessing: (file: File) => Promise<void>;
  onClearUpload: () => void;
}) {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [rawUploadedFile, setRawUploadedFile] = useState<File | null>(null);
  const [rawUploadPreviewUrl, setRawUploadPreviewUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [isCropping, setIsCropping] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cropImageRef = useRef<HTMLImageElement>(null);

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        toast.custom(() => (
          <Toast variant="error">Please select an image file (supported types: ${ALLOWED_FILE_TYPES_MESSAGE})</Toast>
        ));
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > MAX_FILE_SIZE) {
        // TODO(sshader) - share more code with javascript/partner-portal/src/components/tasks/Files.tsx
        toast.custom(() => (
          <Toast variant="error">File size exceeds {MAX_FILE_SIZE_MB}MB. Please upload a smaller file.</Toast>
        ));
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // For other image types, start cropping workflow
      setRawUploadedFile(file);
      const previewUrlForRawUpload = URL.createObjectURL(file);
      setRawUploadPreviewUrl(previewUrlForRawUpload);
      setIsCropping(true);

      if (rawUploadedFile) {
        setUploadedFile(null);
      }
      if (rawUploadPreviewUrl) {
        URL.revokeObjectURL(rawUploadPreviewUrl);
        setRawUploadPreviewUrl(null);
      }
    },
    [rawUploadedFile, rawUploadPreviewUrl],
  );

  const handleClearUpload = useCallback(() => {
    onClearUpload();
    setUploadedFile(null);
    setRawUploadedFile(null);
    setIsCropping(false);
    setCrop(undefined);

    if (rawUploadPreviewUrl) {
      URL.revokeObjectURL(rawUploadPreviewUrl);
      setRawUploadPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [rawUploadPreviewUrl, onClearUpload]);

  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;

    // Set initial crop to center of image with signature aspect ratio
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        TARGET_ASPECT_RATIO,
        width,
        height,
      ),
      width,
      height,
    );
    setCrop(crop);
  }, []);

  const handleCropComplete = useCallback(async () => {
    if (!rawUploadedFile || !crop || !cropImageRef.current) return;

    try {
      const croppedFile = await getCroppedImage(cropImageRef.current, crop);
      const processedFile = await processImageFile(croppedFile);

      setUploadedFile(processedFile);
      await handleSignatureProcessing(processedFile);
    } catch (error) {
      toast.custom(() => <Toast variant="error">Failed to process image. Please try again.</Toast>);
      console.error('Error processing image:', error);
    } finally {
      setIsCropping(false);

      // Clean up raw file URLs
      if (rawUploadPreviewUrl) {
        URL.revokeObjectURL(rawUploadPreviewUrl);
        setRawUploadPreviewUrl(null);
      }
    }
  }, [rawUploadedFile, crop, rawUploadPreviewUrl, handleSignatureProcessing]);

  const handleCancelCrop = useCallback(() => {
    setIsCropping(false);
    setRawUploadedFile(null);
    setCrop(undefined);

    if (rawUploadPreviewUrl) {
      URL.revokeObjectURL(rawUploadPreviewUrl);
      setRawUploadPreviewUrl(null);
    }
  }, [rawUploadPreviewUrl]);

  return (
    <TabContent>
      {!rawUploadedFile && !uploadedFile && (
        <FileUploadContainer>
          <FileUploadText>
            <Typography styledAs="bodyLargeDMSans" weight={600} color="var(--grayscale-600)">
              Select a file to upload
            </Typography>
            <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
              {`Supports ${ALLOWED_FILE_TYPES_MESSAGE} files up to ${MAX_FILE_SIZE_MB}MB`}
            </Typography>
          </FileUploadText>
          <UploadButton variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
            <UploadIcon />
            Upload
          </UploadButton>
        </FileUploadContainer>
      )}

      {isCropping && rawUploadedFile && rawUploadPreviewUrl && (
        <CroppingContainer>
          <UploadToolbar>
            <Typography color="var(--grayscale-600)">Crop your signature</Typography>
            <UploadControls>
              <SecondaryToolbarButton size="sm" variant="secondary" onClick={handleCancelCrop}>
                Cancel
              </SecondaryToolbarButton>
              <Button variant="primary" size="sm" onClick={handleCropComplete}>
                Apply Crop
              </Button>
            </UploadControls>
          </UploadToolbar>

          <CroppingWrapper>
            <ReactCrop crop={crop} onChange={(_, percentCrop) => setCrop(percentCrop)} minHeight={50}>
              <CroppingImage
                ref={cropImageRef}
                src={rawUploadPreviewUrl}
                alt="Crop your signature"
                onLoad={handleImageLoad}
              />
            </ReactCrop>
          </CroppingWrapper>
        </CroppingContainer>
      )}

      {!isCropping && uploadedFile && (
        <PreviewContainer>
          <UploadToolbar>
            {/* Element for spacing */}
            <Typography color="var(--grayscale-600)" />
            <SecondaryToolbarButton variant="secondary" size="sm" onClick={handleClearUpload}>
              Remove
            </SecondaryToolbarButton>
          </UploadToolbar>
          <PreviewWrapper>
            <PreviewImage src={previewUrl ?? undefined} alt="Signature preview" />
          </PreviewWrapper>
        </PreviewContainer>
      )}

      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_FILE_TYPES.join(',')}
        onChange={handleFileSelect}
      />
    </TabContent>
  );
}
